import { LoadingOutlined } from '@ant-design/icons'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Card, Cascader, DatePicker, Form, Input, InputNumber, Select, Switch } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import type { FilteredMetadata } from '@api/useFreight/useFreight'
import type { AutomaticAccessorial, BasicFeeType, ThresholdFeeType } from '../../../../../../../api/useFreight/types'
import {
  isThresholdFeeType,
  isPercentageFeeType,
  getCascaderProductOptions,
  getFeeTypeUnit,
  getThresholdUnit,
  isTimeIntervalFeeType,
} from '../utils'
import type { FormInstance } from 'antd/lib'
import { DEFAULT_TIME_INTERVAL, RATE_INTERVALS } from './constants'
import type { FormValues } from './types'
import { useFormatter } from '@/api/useFormatter'

interface AccessorialInfoCardProps {
  editingAccessorial?: AutomaticAccessorial
  metadataOptions?: FilteredMetadata
  isMetadataLoading: boolean
  form: FormInstance<FormValues>
}

export function AccessorialInfoCard({
  editingAccessorial,
  metadataOptions,
  isMetadataLoading,
  form,
}: AccessorialInfoCardProps) {
  const rawFeeType = Form.useWatch('fee_type', form)
  const currentFeeType = rawFeeType
  const isThresholdType = currentFeeType ? isThresholdFeeType(currentFeeType) : false
  const isTimeFeeType = currentFeeType ? isTimeIntervalFeeType(currentFeeType) : false
  const accessorialMetadata = metadataOptions?.ratebook_options?.accessorials
  const formatters = useFormatter()

  const cascaderProductOptions = React.useMemo(
    () => getCascaderProductOptions(metadataOptions?.product_hierarchy),
    [metadataOptions?.product_hierarchy]
  )

  function handleRateIntervalChange(value: number) {
    form.setFieldsValue({ interval: value })
  }

  return (
    <Card size='small' title='Accessorial Information'>
      <Vertical flex='0' style={{ overflow: 'visible' }}>
        <Vertical gap={8} flex='0' style={{ overflow: 'visible' }}>
          {editingAccessorial ? (
            <Horizontal gap={8} flex='1' style={{ overflow: 'visible' }}>
              <Vertical flex='2' gap={4}>
                <Texto category='label' appearance='hint'>
                  Accessorial Name
                </Texto>
                <Texto weight='bold'>{editingAccessorial.name}</Texto>
              </Vertical>
              <Vertical flex='1' gap={4}>
                <Texto category='label' appearance='hint'>
                  Effective From
                </Texto>
                <Texto weight='bold'>{dayjs(editingAccessorial.rates[0].effective_from).format('MM/DD/YYYY')}</Texto>
              </Vertical>
            </Horizontal>
          ) : (
            <>
              <Horizontal gap={8}>
                <Form.Item
                  name='name'
                  label='Accessorial Name'
                  rules={[{ required: true, message: 'Please enter a name' }]}
                  style={{ flex: 1.5, marginBottom: 8, height: '50%' }}
                >
                  <Input placeholder='Enter accessorial name' />
                </Form.Item>

                <Vertical flex={1} style={{ overflow: 'visible' }}>
                  <Form.Item
                    name='effective_from'
                    label='Effective From'
                    rules={[
                      {
                        required: true,
                        message: 'Please select an effective date',
                      },
                    ]}
                    style={{ marginBottom: 8 }}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Vertical>
              </Horizontal>
              <Horizontal gap={4} alignItems='center' style={{ overflow: 'visible' }}>
                <Form.Item
                  style={{ flex: 1 }}
                  layout='horizontal'
                  name='has_effective_to'
                  label='Set End Date'
                  valuePropName='checked'
                >
                  <Switch className='ml-4' />
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.has_effective_to !== currentValues.has_effective_to
                  }
                  layout='horizontal'
                >
                  {({ getFieldValue }) => {
                    const hasEffectiveTo = getFieldValue('has_effective_to')
                    return hasEffectiveTo ? (
                      <Form.Item
                        layout='horizontal'
                        name='effective_to'
                        label='Effective To'
                        rules={[
                          {
                            required: true,
                            message: 'Please select an end date',
                          },
                        ]}
                      >
                        <DatePicker style={{ width: 260 }} />
                      </Form.Item>
                    ) : null
                  }}
                </Form.Item>
              </Horizontal>
            </>
          )}
        </Vertical>

        <Form.Item name='selected_products' label='Product' style={{ flex: 1, marginBottom: 8 }}>
          <Cascader
            options={cascaderProductOptions}
            multiple
            showCheckedStrategy={Cascader.SHOW_PARENT}
            placeholder={isMetadataLoading ? 'Loading products...' : 'Select Product Group or Products'}
            allowClear
            maxTagCount='responsive'
            style={{ width: '100%' }}
            disabled={isMetadataLoading}
            suffixIcon={isMetadataLoading ? <LoadingOutlined /> : undefined}
            showSearch
          />
        </Form.Item>
        <Horizontal gap={8} flex='0' style={{ overflow: 'visible' }}>
          <Form.Item
            name='fee_type'
            label='Fee Type'
            rules={[{ required: true, message: 'Please select a fee type' }]}
            style={{ flex: isThresholdType ? 0.7 : 1, marginBottom: 8 }}
          >
            <Select
              placeholder='Select fee type'
              showSearch
              allowClear
              style={{ width: '100%' }}
              optionFilterProp='label'
              onChange={() => {
                form.setFieldsValue({ linked_special_pay_config: undefined })
              }}
            >
              {accessorialMetadata?.basic_fee_types && accessorialMetadata?.basic_fee_types.length > 0 && (
                <Select.OptGroup label='Basic Fee Types'>
                  {accessorialMetadata?.basic_fee_types?.map((feeType: BasicFeeType) => (
                    <Select.Option key={feeType} value={feeType} label={feeType}>
                      {feeType}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              )}
              {accessorialMetadata?.threshold_fee_types && accessorialMetadata?.threshold_fee_types.length > 0 && (
                <Select.OptGroup label='Threshold Fee Types'>
                  {accessorialMetadata?.threshold_fee_types?.map((feeType: ThresholdFeeType) => (
                    <Select.Option key={feeType} value={feeType} label={feeType}>
                      {feeType}
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              )}
              {accessorialMetadata?.time_interval_fee_types &&
                accessorialMetadata?.time_interval_fee_types.length > 0 && (
                  <Select.OptGroup label='Time Interval Fee Types'>
                    {accessorialMetadata?.time_interval_fee_types?.map((feeType) => (
                      <Select.Option key={feeType} value={feeType} label={feeType}>
                        {feeType}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                )}
            </Select>
          </Form.Item>

          <Form.Item
            name='rate'
            label={isTimeIntervalFeeType(currentFeeType) ? 'Rate per interval' : 'Rate'}
            rules={[
              { required: true, message: 'Please enter a rate' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (isPercentageFeeType(getFieldValue('fee_type'))) {
                    if (value < 0 || value > 200) {
                      return Promise.reject(new Error('Rate must be between 0 and 200 percent'))
                    }
                  }
                  return Promise.resolve()
                },
              }),
            ]}
            style={{ flex: 1, marginBottom: 8 }}
          >
            <InputNumber
              placeholder='Enter rate'
              precision={isPercentageFeeType(currentFeeType) ? 3 : 5}
              max={isPercentageFeeType(currentFeeType) ? 200 : undefined}
              addonBefore={isPercentageFeeType(currentFeeType) ? null : '$'}
              addonAfter={
                isTimeIntervalFeeType(currentFeeType) ? (
                  <Select
                    options={RATE_INTERVALS}
                    onChange={handleRateIntervalChange}
                    defaultValue={form.getFieldValue('interval') || DEFAULT_TIME_INTERVAL}
                  />
                ) : (
                  getFeeTypeUnit(currentFeeType)
                )
              }
              style={{ width: '100%', flex: isThresholdType ? 3 : 1 }}
            />
          </Form.Item>
          {/* Register interval as a hidden form field so it appears in getFieldsValue/onFinish */}
          <Form.Item name='interval' hidden>
            <InputNumber />
          </Form.Item>
          {isThresholdType && (
            <Form.Item
              name='threshold'
              label='Threshold'
              rules={[{ required: true, message: 'Please enter a threshold' }]}
              style={{ flex: 0.6, marginBottom: 8 }}
            >
              <InputNumber
                placeholder='Enter threshold'
                min={0}
                style={{ width: '100%' }}
                addonAfter={currentFeeType ? getThresholdUnit(currentFeeType) : ''}
              />
            </Form.Item>
          )}
          {isTimeFeeType && (
            <>
              <Form.Item
                name='threshold'
                label='Charge after'
                rules={[{ required: true, message: 'Please enter time' }]}
                style={{ width: 110 }}
              >
                <InputNumber addonAfter={<Texto>min</Texto>} min={0} />
              </Form.Item>
              <Form.Item name='max_intervals' label='Maximum charges' style={{ width: 140 }}>
                <InputNumber addonAfter={<Texto>interval(s)</Texto>} min={0} />
              </Form.Item>
            </>
          )}
        </Horizontal>
        <Form.Item
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.max_intervals !== currentValues.max_intervals || prevValues.rate !== currentValues.rate
          }
          noStyle
        >
          {(formInstance: FormInstance<FormValues>) => {
            const maxIntervals = Number(formInstance.getFieldValue('max_intervals') || 0)
            const rate = Number(formInstance.getFieldValue('rate') || 0)
            const total = maxIntervals * rate
            if (!maxIntervals) return null
            return (
              <Texto
                style={{
                  border: '1px solid var(--theme-color-2)',
                  backgroundColor: 'var(--theme-color-2-dim)',
                  borderRadius: 6,
                }}
                className='px-3 py-2'
              >
                <b>Maximum Charge:</b> {formatters.formatCurrency(total, true, 2)}
              </Texto>
            )
          }}
        </Form.Item>
      </Vertical>
    </Card>
  )
}
